import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Flame, CheckCircle, Loader2, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, checkAuth } = useAuth();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    pollPaymentStatus();
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    if (attempts >= 5) {
      setStatus('error');
      return;
    }

    try {
      const { data } = await axios.get(`${API}/payment/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (data.payment_status === 'paid') {
        setStatus('success');
        // Refresh user data
        await checkAuth();
        return;
      }

      if (data.status === 'expired') {
        setStatus('error');
        return;
      }

      // Still processing, poll again
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    } catch (e) {
      setAttempts(prev => prev + 1);
      setTimeout(pollPaymentStatus, 2000);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center px-6">
      <div className="text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 text-rose-500 animate-spin mx-auto mb-6" />
            <h1 className="font-outfit text-2xl font-medium text-white mb-2">
              Processing Payment...
            </h1>
            <p className="text-zinc-500">Please wait while we confirm your payment</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
            <h1 className="font-outfit text-3xl font-medium text-white mb-2">
              Welcome to LocalFlame!
            </h1>
            <p className="text-zinc-400 mb-8">
              Your payment was successful. All features are now unlocked!
            </p>
            <Button
              data-testid="start-matching-btn"
              onClick={() => navigate('/app')}
              className="bg-rose-600 hover:bg-rose-500 text-white font-semibold px-8 py-6 rounded-full text-lg"
            >
              Start Matching
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-red-500" />
            </div>
            <h1 className="font-outfit text-2xl font-medium text-white mb-2">
              Payment Failed
            </h1>
            <p className="text-zinc-400 mb-8">
              We couldn't confirm your payment. Please try again.
            </p>
            <Button
              data-testid="try-again-btn"
              onClick={() => navigate('/paywall')}
              className="bg-rose-600 hover:bg-rose-500 text-white font-semibold px-8 py-6 rounded-full"
            >
              Try Again
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
